<template>
  <v-row>
    <v-col cols="12" :class="isStudent ? '' : 'py-0'">
      <v-text-field
        id="profile-address1"
        v-model="address1"
        label="Your Address"
        type="text"
        persistent-hint
        :error-messages="addressOneErrorMessages"
        density="compact"
        :prepend-icon="isStudent ? 'fa-light fa-location-dot' : ''"
        @update:model-value="v$.address1.$touch()"
        @blur="v$.address1.$touch()"
      />
    </v-col>
    <v-col cols="12" class="py-0">
      <v-text-field
        id="profile-address2"
        v-model="address2"
        class="pl-10"
        label="Your Address 2"
        type="text"
        density="compact"
        @update:model-value="updateAddress2"
      />
    </v-col>
    <v-col cols="12" class="py-0">
      <v-text-field
        id="profile-city"
        v-model="city"
        class="pl-10"
        label="City"
        type="text"
        density="compact"
        persistent-hint
        :error-messages="cityErrorMessages"
        @update:model-value="v$.city.$touch()"
        @blur="v$.city.$touch()"
      />
    </v-col>
    <v-col cols="12" md="4" class="py-0">
      <!-- The prefix &nbsp is a clever way to populate component with invisible text for a consistent height -->
      <v-select
        id="profile-country"
        v-model="country"
        :class="isStudent ? '' : 'pl-10'"
        :items="$countries"
        label="Country"
        density="compact"
        persistent-hint
        prefix="&nbsp"
        :prepend-icon="isStudent ? 'fa-light fa-location-dot' : ''"
        menu-icon="fa-solid fa-chevron-down"
        @update:model-value="updateCountry"
      />
    </v-col>
    <v-col cols="12" md="4" :class="isStudent ? '' : 'py-0'">
      <!-- The prefix &nbsp is a clever way to populate component with invisible text for a consistent height -->
      <v-select
        id="profile-student-state"
        v-model="state"
        class="capitalize"
        :items="showProvinces ? $provinces : $states"
        :label="(showProvinces ? 'Province' : $t('text.state')) + ' (current location)'"
        density="compact"
        :hint="
          showStates || showProvinces
            ? `We show ${$t(
                'text.provider'
              )}s who are appropriate for you based on your location.`
            : 'Based on the country you selected, this field is not necessary'
        "
        persistent-hint
        prefix="&nbsp"
        :prepend-icon="isStudent ? 'fa-light fa-location-dot' : ''"
        :disabled="!showStates && !showProvinces"
        menu-icon="fa-solid fa-chevron-down"
        @update:model-value="updateState"
      />
    </v-col>
    <v-col sm="12" md="4" :class="isStudent ? '' : 'py-0'">
      <v-text-field
        id="profile-zipcode"
        v-model="zipcode"
        class="capitalize"
        :label="$t('text.zip')"
        type="text"
        density="compact"
        persistent-hint
        :prepend-icon="isStudent ? 'fa-light fa-location-dot' : ''"
        :max-length="$t('text.max_length')"
        :min-length="$t('text.max_length')"
        :error-messages="zipcodeErrorMessages"
        @update:model-value="updateZipcode"
        @blur="v$.zipcode.$touch()"
      />
    </v-col>
  </v-row>
</template>
<script>
import { useVuelidate } from "@vuelidate/core";
import { uwillProfileCategoryMixin } from "./profile-category.js";

export default {
  name: "ProfileLocation",
  mixins: [uwillProfileCategoryMixin],
  props: [
    "zipcodeValidated",
    "addressOneValidated",
    "addressTwoValidated",
    "cityValidated",
    "stateValidated",
    "countryValidated",
    "isStudent"
  ],
  emits: ["addressOneValidated", "cityValidated", "countryValidated", "zipcodeValidated"],
  setup() {
    return { v$: useVuelidate() };
  },
  data: () => ({
    zipcode: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    country: ""
  }),
  computed: {
    addressOneErrorMessages() {
      this.address1;
      let errors = [];
      if (this.v$.address1 != null && !this.v$.address1.$dirty) {
        return errors;
      }

      if (this.v$.address1.required.$invalid) {
        errors.push("Address 1 is required");
      }
      let emitValue = null;
      if (errors.length == 0) {
        // TODO: Confirm this $model still works after Vue 3 migration (here and elsewhere)
        emitValue = this.v$.address1.$model;
      }
      this.$emit("update:addressOneValidated", emitValue);
      return errors;
    },
    cityErrorMessages() {
      let errors = [];
      if (this.v$.city != null && !this.v$.city.$dirty) {
        return errors;
      }

      if (this.v$.city.required.$invalid) {
        errors.push("City is required");
      }

      let emitValue = null;
      if (errors.length == 0) {
        emitValue = this.v$.city.$model;
      }
      this.$emit("update:cityValidated", emitValue);
      return errors;
    },
    zipcodeErrorMessages() {
      const caZipcodeRegex = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i;
      const ukPostcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;
      const usZipcodeRegex = /^\d{5}(-\d{4})?$/i;

      const errorMessage = this.$t("text.zip") + " is not valid";
      const minLength = 3;
      const trimmedZipcode = this.zipcode.trim();
      let errors = [];

      if (this.v$.zipcode != null && !this.v$.zipcode.$dirty) {
        return errors;
      }

      // Required
      if (this.v$.zipcode.required.$invalid) {
        errors.push(this.$t("text.zip") + " is required");
      }
      if (
        this.country === "United States" &&
        usZipcodeRegex.test(trimmedZipcode) === false
      ) {
        // Validate US
        // US: 12345 or 12345-6789
        errors.push(errorMessage);
      } else if (
        this.country === "Canada" &&
        caZipcodeRegex.test(trimmedZipcode) === false
      ) {
        // Validate Canadian postal codes
        // Canada: A1A 1A1 or A1A1A1
        errors.push(errorMessage);
      } else if (
        this.country === "United Kingdom" &&
        ukPostcodeRegex.test(trimmedZipcode) === false
      ) {
        // Validate UK postcodes
        // UK: A1 1AA or A11 1AA
        errors.push(errorMessage);
      } else {
        // Other countries - length validation only
        if (trimmedZipcode.length < minLength) {
          errors.push(errorMessage);
        }
      }

      let emitValue = null;
      if (errors.length == 0) {
        emitValue = this.v$.zipcode.$model;
      }
      this.$emit("update:zipcodeValidated", emitValue);
      return errors;
    },
    showProvinces() {
      return this.country == "Canada";
    },
    showStates() {
      return this.country == "United States";
    }
  },
  watch: {
    graduationDateMenu(val) {
      val && setTimeout(() => (this.activePicker = "MONTH"));
    },
    skip_graduation_date(val) {
      if (val) {
        this.updateGraduationDate(false);
      } else {
        this.updateGraduationDate(this.graduationDate);
      }
    }
  },
  mounted: function() {
    this.zipcode = this.zipcodeValidated;
    this.address1 = this.addressOneValidated;
    this.address2 = this.addressTwoValidated;
    this.city = this.cityValidated;
    this.state = this.stateValidated;
    this.country = this.countryValidated;
  },
  methods: {
    updateCountry(event) {
      this.$emit("update:countryValidated", event);
    },
    updateAddress2(event) {
      this.$emit("update:addressTwoValidated", event);
    },
    updateZipcode(event) {
      this.$emit("update:zipcodeValidated", event);
    },
    updateState(event) {
      this.$emit("update:stateValidated", event);
    }
  }
};
</script>
